<script setup lang="ts">
const { customer, isLoggedIn } = useAuth()

onMounted(() => {
  if (!isLoggedIn.value) navigateTo('/account/login')
})

const profile = reactive({ firstName: '', lastName: '', email: '', phone: '' })
const profileSubmitting = ref(false)
const profileSuccess = ref(false)
const profileError = ref('')

watchEffect(() => {
  if (customer.value) {
    profile.firstName = customer.value.firstName ?? ''
    profile.lastName = customer.value.lastName ?? ''
    profile.email = customer.value.email ?? ''
    profile.phone = (customer.value as any).phone ?? ''
  }
})

async function saveProfile() {
  profileError.value = ''
  profileSuccess.value = false
  if (!profile.firstName.trim() || !profile.lastName.trim() || !profile.email.trim()) {
    profileError.value = 'First name, last name and email are required.'
    return
  }
  profileSubmitting.value = true
  try {
    await $fetch('/api/customer/update', {
      method: 'PATCH',
      body: { firstName: profile.firstName, lastName: profile.lastName, email: profile.email, phone: profile.phone },
    })
    profileSuccess.value = true
    await $fetch('/api/auth/me')
  }
  catch (err: any) {
    profileError.value = err?.data?.statusMessage ?? 'Something went wrong.'
  }
  finally {
    profileSubmitting.value = false
  }
}

const password = reactive({ current: '', next: '', confirm: '' })
const passwordSubmitting = ref(false)
const passwordSuccess = ref(false)
const passwordError = ref('')

async function changePassword() {
  passwordError.value = ''
  passwordSuccess.value = false
  if (!password.next || password.next.length < 5) {
    passwordError.value = 'Password must be at least 5 characters.'
    return
  }
  if (password.next !== password.confirm) {
    passwordError.value = 'Passwords do not match.'
    return
  }
  passwordSubmitting.value = true
  try {
    await $fetch('/api/customer/update', { method: 'PATCH', body: { password: password.next } })
    passwordSuccess.value = true
    password.current = ''
    password.next = ''
    password.confirm = ''
  }
  catch (err: any) {
    passwordError.value = err?.data?.statusMessage ?? 'Something went wrong.'
  }
  finally {
    passwordSubmitting.value = false
  }
}

const inputClass = 'bg-surface border border-stroke h-11 font-sans text-sm text-dark placeholder:text-muted px-3.5 rounded-lg outline-none focus:border-brand transition-colors w-full'
</script>

<template>
  <div class="bg-page flex flex-col items-start">
    <!-- Header -->
    <div class="flex flex-col gap-2 items-start pb-10 pt-14 px-4 md:px-20 w-full">
      <p class="font-sans text-[13px] text-muted">
        <NuxtLink to="/account" class="hover:text-secondary transition-colors">Account</NuxtLink>
        &nbsp;/&nbsp; Account Details
      </p>
      <p class="font-serif font-semibold text-4xl md:text-[42px] text-dark">Account Details</p>
    </div>

    <!-- Main -->
    <div class="flex flex-col md:flex-row gap-12 items-start pb-24 px-4 md:px-20 w-full">
      <!-- Sidebar -->
      <div class="flex md:flex-col gap-1 items-start shrink-0 w-full md:w-[220px] overflow-x-auto">
        <NuxtLink to="/account" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Dashboard</p>
        </NuxtLink>
        <NuxtLink to="/account/orders" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Order History</p>
        </NuxtLink>
        <NuxtLink to="/account/addresses" class="flex items-center px-4 py-3 rounded-lg shrink-0 hover:bg-surface transition-colors">
          <p class="font-sans text-[15px] text-secondary whitespace-nowrap">Addresses</p>
        </NuxtLink>
        <div class="bg-surface flex items-center px-4 py-3 rounded-lg shrink-0">
          <p class="font-sans font-medium text-[15px] text-dark whitespace-nowrap">Account Details</p>
        </div>
      </div>

      <!-- Content -->
      <div class="flex flex-1 flex-col gap-8 items-start min-w-0 w-full">

        <!-- Profile -->
        <div class="bg-surface flex flex-col gap-5 items-start p-7 rounded-xl w-full">
          <p class="font-serif font-semibold text-[22px] text-dark">Personal Information</p>
          <div class="flex flex-col md:flex-row gap-3 w-full">
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">First Name</label>
              <input v-model="profile.firstName" type="text" placeholder="Jane" :class="inputClass" />
            </div>
            <div class="flex flex-col gap-1.5 flex-1">
              <label class="font-label font-light text-[13px] text-secondary">Last Name</label>
              <input v-model="profile.lastName" type="text" placeholder="Doe" :class="inputClass" />
            </div>
          </div>
          <div class="flex flex-col gap-1.5 w-full">
            <label class="font-label font-light text-[13px] text-secondary">Email</label>
            <input v-model="profile.email" type="email" placeholder="you@example.com" :class="inputClass" />
          </div>
          <div class="flex flex-col gap-1.5 w-full">
            <label class="font-label font-light text-[13px] text-secondary">Phone</label>
            <input v-model="profile.phone" type="tel" placeholder="+62 812 3456 7890" :class="inputClass" />
          </div>
          <p v-if="profileError" class="font-sans text-[13px] text-error">{{ profileError }}</p>
          <p v-if="profileSuccess" class="font-sans text-[13px] text-success">Profile updated successfully.</p>
          <button
            class="bg-primary px-5 py-2.5 rounded-md font-label text-sm text-white disabled:opacity-60"
            :disabled="profileSubmitting"
            @click="saveProfile"
          >{{ profileSubmitting ? 'Saving…' : 'Save Changes' }}</button>
        </div>

        <!-- Password -->
        <div class="bg-surface flex flex-col gap-5 items-start p-7 rounded-xl w-full">
          <p class="font-serif font-semibold text-[22px] text-dark">Change Password</p>
          <div class="flex flex-col gap-1.5 w-full">
            <label class="font-label font-light text-[13px] text-secondary">New Password</label>
            <input v-model="password.next" type="password" placeholder="Min. 5 characters" :class="inputClass" />
          </div>
          <div class="flex flex-col gap-1.5 w-full">
            <label class="font-label font-light text-[13px] text-secondary">Confirm New Password</label>
            <input v-model="password.confirm" type="password" placeholder="Repeat new password" :class="inputClass" />
          </div>
          <p v-if="passwordError" class="font-sans text-[13px] text-error">{{ passwordError }}</p>
          <p v-if="passwordSuccess" class="font-sans text-[13px] text-success">Password changed successfully.</p>
          <button
            class="bg-primary px-5 py-2.5 rounded-md font-label text-sm text-white disabled:opacity-60"
            :disabled="passwordSubmitting"
            @click="changePassword"
          >{{ passwordSubmitting ? 'Saving…' : 'Change Password' }}</button>
        </div>

      </div>
    </div>

  </div>
</template>
